/**
 * Coda offline minimale su IndexedDB (nessuna dipendenza esterna).
 *
 * Serve a non perdere dati creati senza rete — tipicamente le immersioni registrate
 * in barca. Gli elementi sono generici per `kind`, così la stessa coda può servire
 * più tipi di operazione differita. Tutto è best-effort: in finestra privata o con
 * IndexedDB bloccato le funzioni rigettano e i chiamanti gestiscono il fallback.
 */

const DB_NAME = 'edive-offline'
const DB_VERSION = 1
const STORE = 'queue'

export interface QueuedItem<T = unknown> {
  id: string
  kind: string
  payload: T
  createdAt: number
}

function newId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  } catch { /* ignore */ }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(new Error('IndexedDB non disponibile')); return }
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: 'id' })
        os.createIndex('kind', 'kind', { unique: false })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function store(db: IDBDatabase, mode: IDBTransactionMode): IDBObjectStore {
  return db.transaction(STORE, mode).objectStore(STORE)
}

export async function enqueue<T>(kind: string, payload: T): Promise<QueuedItem<T>> {
  const item: QueuedItem<T> = { id: newId(), kind, payload, createdAt: Date.now() }
  const db = await openDB()
  try {
    await new Promise<void>((res, rej) => {
      const r = store(db, 'readwrite').add(item)
      r.onsuccess = () => res()
      r.onerror = () => rej(r.error)
    })
  } finally { db.close() }
  return item
}

export async function allByKind<T>(kind: string): Promise<QueuedItem<T>[]> {
  const db = await openDB()
  try {
    const out = await new Promise<QueuedItem<T>[]>((res, rej) => {
      const acc: QueuedItem<T>[] = []
      const r = store(db, 'readonly').openCursor()
      r.onsuccess = () => {
        const cursor = r.result
        if (cursor) {
          if ((cursor.value as QueuedItem<T>).kind === kind) acc.push(cursor.value as QueuedItem<T>)
          cursor.continue()
        } else res(acc)
      }
      r.onerror = () => rej(r.error)
    })
    return out.sort((a, b) => a.createdAt - b.createdAt)
  } finally { db.close() }
}

export async function remove(id: string): Promise<void> {
  const db = await openDB()
  try {
    await new Promise<void>((res, rej) => {
      const r = store(db, 'readwrite').delete(id)
      r.onsuccess = () => res()
      r.onerror = () => rej(r.error)
    })
  } finally { db.close() }
}

export async function countByKind(kind: string): Promise<number> {
  return (await allByKind(kind)).length
}
